import React, { useState } from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import type { CustomCellRendererProps } from 'ag-grid-react';
import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import { useReceiveStore } from '~/store';
import { formatNumber } from '~/utils';

import { MAX_RECEIVE_REQUEST_PRODUCT_COUNT } from '~/constants';

import type {
  TypeReceiveItemEditableFields,
  TypeReceiveRequestItemErrorState,
  TypeReceiveRequestRegisterModalGoods,
} from '~/types/receive.types';

import styles from './ReceiveRequestQuantityInputCell.module.scss';

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 수량 관련 Input 셀 렌더러 (수량(BOX), 입수(BOX당), 수량(EA))
 */
const ReceiveRequestQuantityInputCell = ({
  node: { rowIndex },
  column,
}: CustomCellRendererProps<TypeReceiveRequestRegisterModalGoods>) => {
  const [requestModalGoodsList, setRequestModalGoodsList, requestModalGoodsListStatus, setRequestModalGoodsListStatus] =
    useReceiveStore(
      useShallow((state) => [
        state.requestModalGoodsList,
        state.setRequestModalGoodsList,
        state.requestModalGoodsListStatus,
        state.setRequestModalGoodsListStatus,
      ])
    );

  const colId = column?.getColId() as TypeReceiveItemEditableFields;
  const rowData = requestModalGoodsList[rowIndex as number];
  const cellData = rowData?.[colId] ? Number(rowData[colId]) : '';

  const [shouldValidateOnBlur, setShouldValidateOnBlur] = useState(false);

  if (requestModalGoodsList.length === 0 || rowIndex === null || column === undefined || rowData === undefined) {
    return <div>-</div>;
  }

  const getUpdatedRowTotalQty = (updatedValue: number) => {
    // 총 수량 = 수량(BOX)*입수(BOX당)+수량(EA)
    const boxQty = rowData.boxQty ?? 0;
    const boxPerQty = rowData.boxPerQty ?? 0;
    const eachQty = rowData.eachQty ?? 0;
    let updatedRowTotalQty = 0;

    if (colId === 'boxQty') {
      updatedRowTotalQty = updatedValue * boxPerQty + eachQty;
    } else if (colId === 'boxPerQty') {
      updatedRowTotalQty = boxQty * updatedValue + eachQty;
    } else if (colId === 'eachQty') {
      updatedRowTotalQty = boxQty * boxPerQty + updatedValue;
    }

    return updatedRowTotalQty;
  };

  const getUpdatedTotalQty = (updatedValue: number) => {
    // 기존의 전체 총 수량에서, 현재 row의 총수량을 입력받은 값으로 업데이트 해 반환
    const prevTotalQty = requestModalGoodsList.reduce((acc, item) => {
      return acc + (item.totalQty ?? 0);
    }, 0);
    const prevRowTotalQty = rowData.totalQty ?? 0;

    return prevTotalQty - prevRowTotalQty + getUpdatedRowTotalQty(updatedValue);
  };

  const resetCellData = () => {
    setRequestModalGoodsList([
      ...requestModalGoodsList.slice(0, rowIndex),
      {
        ...rowData,
        [colId]: 0,
        totalQty: getUpdatedRowTotalQty(0),
      },
      ...requestModalGoodsList.slice(rowIndex + 1),
    ]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const numberValue = Number(value);

    if (!Number.isInteger(numberValue)) {
      return;
    }

    if (colId === 'boxPerQty' && value === '0') {
      dialogService.addDialog({
        children: '입수(BOX당)은 최소 1 이상 입력해야 합니다.',
      });
    }

    setRequestModalGoodsList([
      ...requestModalGoodsList.slice(0, rowIndex),
      {
        ...rowData,
        [colId]: numberValue,
        totalQty: getUpdatedRowTotalQty(numberValue),
      },
      ...requestModalGoodsList.slice(rowIndex + 1),
    ]);

    setRequestModalGoodsListStatus(
      requestModalGoodsListStatus.map((rowError, index) => {
        if (index === rowIndex) {
          return {
            ...rowError,
            ...((colId === 'boxQty' || colId === 'eachQty') && {
              boxQty: 'default',
              eachQty: 'default',
            }),
            ...(colId === 'boxPerQty' && { boxPerQty: 'default' }),
          };
        } else {
          return rowError;
        }
      })
    );

    setShouldValidateOnBlur(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!shouldValidateOnBlur) return;

    const value = e.target.value.trim();
    const numberValue = Number(value);

    // 수량(EA) > 입수(BOX당) 입력
    if (colId === 'eachQty' && numberValue > (rowData.boxPerQty ?? 0)) {
      dialogService.addDialog({
        children: '수량(EA)는 입수(BOX당)보다 적은 수량만\n입력할 수 있습니다.',
        onConfirm: resetCellData,
      });
    }
    // 입수(BOX당) < 수량(EA) 입력
    else if (colId === 'boxPerQty' && numberValue > 0 && numberValue < (rowData.eachQty ?? 0)) {
      dialogService.addDialog({
        children: '입수(BOX당)는 수량(EA)보다 큰 수량만\n입력할 수 있습니다.',
        onConfirm: resetCellData,
      });
    }
    // 총 수량 20,000 초과
    else if (getUpdatedTotalQty(numberValue) > MAX_RECEIVE_REQUEST_PRODUCT_COUNT) {
      dialogService.addDialog({
        children: `한 번에 입고 요청할 수 있는\n총 수량은 최대 ${formatNumber(
          MAX_RECEIVE_REQUEST_PRODUCT_COUNT
        )}개 입니다.`,
        onConfirm: resetCellData,
      });
    }

    setShouldValidateOnBlur(false);
  };

  return (
    <div className={styles['kitchen-component-cell']}>
      <NumberUnitInput
        size="tiny"
        placeholder="입력"
        maxLength={7}
        disabled={colId === 'boxPerQty' && !rowData.boxQty}
        status={requestModalGoodsListStatus[rowIndex]?.[colId as keyof TypeReceiveRequestItemErrorState] ?? 'default'}
        value={cellData}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default ReceiveRequestQuantityInputCell;
